import React, { useState, useEffect } from "react";
import "./LoginPop.css";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../../../context/Authcontext";

/* ── helpers ── */
const isValidEmail   = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isValidPhone   = (v) => /^[+]?[\d\s\-().]{7,15}$/.test(v.trim());
const isEmailOrPhone = (v) => isValidEmail(v) || isValidPhone(v.replace(/\D/g, ""));

/* ── countdown hook ── */
function useCountdown(seconds, active) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    if (!active) { setLeft(seconds); return; }
    setLeft(seconds);
    const iv = setInterval(() => setLeft(p => {
      if (p <= 1) { clearInterval(iv); return 0; }
      return p - 1;
    }), 1000);
    return () => clearInterval(iv);
  }, [active, seconds]); // eslint-disable-line
  return left;
}

const LoginPop   = ({ isOpen, onClose, onLoginSuccess }) => {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const returnTo = location.state?.from
    ? `${location.state.from.pathname || ""}${location.state.from.search || ""}${location.state.from.hash || ""}`
    : "";
  const signupPath = location.pathname.includes("/employer")
    ? "/employer/signup"
    : "/employee/signup";

  /* ── login state ── */
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [errorType,    setErrorType]    = useState("");

  /* ── forgot password state ── */
  const [showForgot,       setShowForgot]       = useState(false);
  const [forgotStep,       setForgotStep]       = useState("input");
  const [forgotInput,      setForgotInput]      = useState("");
  const [forgotLoading,    setForgotLoading]    = useState(false);
  const [forgotMsg,        setForgotMsg]        = useState("");
  const [forgotError,      setForgotError]      = useState("");
  const [sentTo,           setSentTo]           = useState("");
  const [resendCount,      setResendCount]      = useState(0);
  const [countdownActive,  setCountdownActive]  = useState(false);
  const secondsLeft = useCountdown(60, countdownActive);

  useEffect(() => {
    if (secondsLeft === 0) setCountdownActive(false);
  }, [secondsLeft]);

const persistSession = (token, user) => {
  try {
    localStorage.setItem("th_employer_token", token);             
    localStorage.setItem("token", token);                          
    localStorage.setItem("user",      JSON.stringify(user));
    localStorage.setItem("th_user",   JSON.stringify(user));
    localStorage.setItem("th_employer", JSON.stringify(user));   
  } catch {}
};
  /* ── post-login routing ── */
  const handleSuccess = (token, user) => {
    persistSession(token, user);
    login(token, user); // sync AuthContext

    if (user.role === "admin") {
      if (onClose) onClose();
      navigate("/admin");
      return;
    }
    if (returnTo) {
      if (onClose) onClose();
      navigate(returnTo, { replace: true });
      return;
    }
    if (typeof onLoginSuccess === "function") {
      onLoginSuccess({ token, user });
      return;
    }
    if (onClose) onClose();
    if (user.role === "employer") navigate("/employer/recruiter-dashboard");
    else                          navigate("/employee/profile");
  };

  /* ── normal login ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setErrorType("");
    if (!email.trim() || !password) {
      setError("Please enter your email / username and password.");
      setErrorType("general");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/api/login", {
        email: email.trim(),
        password,
      });
      handleSuccess(data.token, data.user);
    } catch (err) {
      const status  = err.response?.status;
      const message = err.response?.data?.error || "Login failed. Please try again.";
      if      (status === 404) { setErrorType("email");    setError(message); }
      else if (status === 401) { setErrorType("password"); setError(message); }
      else                     { setErrorType("general");  setError(message); }
    } finally {
      setLoading(false);
    }
  };

  /* ── google login ── */
  const handleGoogleSuccess = async (credentialResponse) => {
    setError(""); setErrorType(""); setLoading(true);
    try {
      const { data } = await axios.post("/api/login/google", {
        credential: credentialResponse.credential,
      });
      handleSuccess(data.token, data.user);
    } catch {
      setError("Google login failed. Please try again.");
      setErrorType("general");
    } finally {
      setLoading(false);
    }
  };

  /* ── forgot password ── */
  const handleForgotSubmit = async (e) => {
    e?.preventDefault();
    setForgotError(""); setForgotMsg("");
    const val = forgotInput.trim();
    if (!val)               { setForgotError("Please enter your email or phone number."); return; }
    if (!isEmailOrPhone(val)) { setForgotError("Please enter a valid email or phone number."); return; }
    if (resendCount >= 3)   { setForgotError("Too many attempts. Please contact support."); return; }
    setForgotLoading(true);
    try {
      const isEmail = isValidEmail(val);
      const { data } = await axios.post("/api/forgot-password",
        isEmail ? { email: val } : { mobile: val }
      );
      setSentTo(val);
      setForgotStep("sent");
      setResendCount(c => c + 1);
      setCountdownActive(true);
      setForgotMsg(data.devResetLink
        ? `DEV — Reset link: ${data.devResetLink}`
        : data.message || (isEmail
            ? `Reset link sent to ${val}.`
            : `Reset OTP sent to ${val}.`)
      );
    } catch (err) {
      const status  = err.response?.status;
      const message = err.response?.data?.error;
      if      (status === 404) setForgotError("No account found with this email or phone.");
      else if (status === 429) { setForgotError("Too many requests. Please wait."); setCountdownActive(true); }
      else if (status === 400) setForgotError(message || "Invalid email or phone.");
      else if (!err.response)  setForgotError("Network error. Check your connection.");
      else                     setForgotError(message || "Something went wrong.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResend = () => {
    if (countdownActive || forgotLoading) return;
    setForgotMsg(""); setForgotError("");
    handleForgotSubmit();
  };

  const closeForgot = () => {
    setShowForgot(false); setForgotStep("input"); setForgotInput("");
    setForgotMsg(""); setForgotError(""); setSentTo("");
    setResendCount(0); setCountdownActive(false);
  };

  const handleRegister = (e, path) => {
    e.preventDefault();
    if (onClose) onClose();
    navigate(path);
  };

  if (!isOpen) return null;

  /* ════════════════════════════════════
     FORGOT PASSWORD VIEW
  ════════════════════════════════════ */
  if (showForgot) return (
    <>
      <div className="lp-overlay" onClick={onClose} />
      <div className="lp-modal">

        {/* header */}
        <div className="lp-header">
          <button className="lp-close" onClick={onClose}>✕</button>
          <div className="lp-brand">
            <div className="lp-hex">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5"/>
                <line x1="12" y1="2" x2="12" y2="22"/>
                <line x1="2" y1="8.5" x2="22" y2="8.5"/>
                <line x1="2" y1="15.5" x2="22" y2="15.5"/>
              </svg>
            </div>
            <span className="lp-brand-name">TalentHive</span>
          </div>
          <h2 className="lp-title">Reset password</h2>
          <p className="lp-subtitle">We'll send a reset link to your email or phone</p>
        </div>

        {/* body */}
        <div className="lp-body">
          {forgotStep === "input" && (
            <>
              <button className="lp-back-link" onClick={closeForgot}>← Back to login</button>

              {forgotMsg   && <div className="lp-success-banner">✓ {forgotMsg}</div>}
              {forgotError && <div className="lp-error-banner"><span className="lp-err-icon">!</span>{forgotError}</div>}

              <form onSubmit={handleForgotSubmit} noValidate>
                <div className="lp-field">
                  <label className="lp-label">Email address or phone number</label>
                  <input
                    type="text"
                    className={`lp-input${forgotError ? " lp-input--err" : ""}`}
                    placeholder="e.g. priya@email.com or +91 98765 43210"
                    value={forgotInput}
                    onChange={e => { setForgotInput(e.target.value); setForgotError(""); setForgotMsg(""); }}
                    disabled={forgotLoading}
                    autoFocus
                  />
                  {forgotInput && !isEmailOrPhone(forgotInput) && (
                    <span className="lp-hint">Enter a valid email or phone number</span>
                  )}
                </div>
                <button
                  type="submit"
                  className="lp-btn-primary"
                  disabled={forgotLoading || !forgotInput.trim()}
                >
                  {forgotLoading
                    ? <span className="lp-spinner-wrap"><span className="lp-spinner"/>Sending...</span>
                    : "Send reset link"
                  }
                </button>
              </form>
            </>
          )}

          {forgotStep === "sent" && (
            <>
              <div className="lp-sent-icon">📬</div>
              <h3 className="lp-sent-title">Check your inbox</h3>
              <p className="lp-sent-sub">We sent a reset link to</p>
              <p className="lp-sent-to">{sentTo}</p>

              {forgotMsg   && <div className="lp-success-banner" style={{ wordBreak: "break-all" }}>✓ {forgotMsg}</div>}
              {forgotError && <div className="lp-error-banner"><span className="lp-err-icon">!</span>{forgotError}</div>}

              <div className="lp-tips">
                <p className="lp-tips-title">Didn't receive it?</p>
                <ul>
                  <li>Check your spam / junk folder</li>
                  <li>Make sure you used the correct email</li>
                  <li>Wait up to 2 minutes for delivery</li>
                </ul>
              </div>

              <button
                className="lp-btn-outline"
                onClick={handleResend}
                disabled={countdownActive || forgotLoading || resendCount >= 3}
              >
                {forgotLoading
                  ? <span className="lp-spinner-wrap"><span className="lp-spinner"/>Sending...</span>
                  : countdownActive   ? `Resend in ${secondsLeft}s`
                  : resendCount >= 3  ? "Max resends reached"
                  : "Resend reset link"
                }
              </button>

              <div className="lp-forgot-footer">
                <button className="lp-text-btn" onClick={() => { setForgotStep("input"); setForgotMsg(""); setForgotError(""); }}>
                  ← Change email / phone
                </button>
                <button className="lp-text-btn" onClick={closeForgot}>Back to login</button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );

  /* ════════════════════════════════════
     MAIN LOGIN VIEW
  ════════════════════════════════════ */
  return (
    <>
      <div className="lp-overlay" onClick={onClose} />
      <div className="lp-modal">

        {/* header */}
        <div className="lp-header">
          <button className="lp-close" onClick={onClose}>✕</button>
          <div className="lp-brand">
            <div className="lp-hex">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5"/>
                <line x1="12" y1="2" x2="12" y2="22"/>
                <line x1="2" y1="8.5" x2="22" y2="8.5"/>
                <line x1="2" y1="15.5" x2="22" y2="15.5"/>
              </svg>
            </div>
            <span className="lp-brand-name">TalentHive</span>
          </div>
          <h2 className="lp-title">Welcome back</h2>
          <p className="lp-subtitle">Sign in to access your account</p>
        </div>

        {/* body */}
        <div className="lp-body">

          {/* error banner */}
          {error && (
            <div className={`lp-error-banner${errorType === "password" ? " lp-error-banner--warn" : ""}`}>
              <span className="lp-err-icon">!</span>
              {error}
              {errorType === "email" && (
                <a
                  href={signupPath}
                  className="lp-err-link"
                  onClick={e => handleRegister(e, signupPath)}
                >
                  Sign up free →
                </a>
              )}
            </div>
          )}

          {/* login form */}
          <form onSubmit={handleLogin} noValidate>
            <div className="lp-field">
              <label className="lp-label">Email or username</label>
              <input
                type="text"
                className={`lp-input${errorType === "email" ? " lp-input--err" : ""}`}
                placeholder="Enter your email or username"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(""); setErrorType(""); }}
                disabled={loading}
                autoComplete="username"
              />
            </div>

            <div className="lp-field">
              <label className="lp-label">Password</label>
              <div className="lp-pw-wrap">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`lp-input lp-input--pw${errorType === "password" ? " lp-input--err" : ""}`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); setErrorType(""); }}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lp-pw-eye"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>
            </div>

            <div className="lp-forgot-row">
              <button
                type="button"
                className="lp-text-btn"
                onClick={() => { setShowForgot(true); setError(""); }}
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" className="lp-btn-primary" disabled={loading}>
              {loading
                ? <span className="lp-spinner-wrap"><span className="lp-spinner"/>Logging in...</span>
                : "Login"
              }
            </button>
          </form>

          {/*
          <div className="lp-divider"><span>or continue with</span></div>
          <div className="lp-google-wrap">
            Google sign-in is disabled for now.
          </div>
          */}

          {/* register options */}
          <p className="lp-reg-text">Don't have an account?</p>
          <div className="lp-reg-btns">
            <button
              className="lp-reg-btn"
              onClick={e => handleRegister(e, "/employee/signup")}
            >
              🧑‍💼 Candidate
            </button>
            <button
              className="lp-reg-btn"
              onClick={e => handleRegister(e, "/employer/signup")}
            >
              🏢 Recruiter
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default LoginPop;
